import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { serviceCatalogService } from '../../services/serviceCatalogService';
import ReminderDaysEditor from './ReminderDaysEditor';

/** Mirror the backend caps (appointment-reminder-rules.ts) so nothing surprises on save. */
const MAX_LEADS = 3;
const MAX_LEAD_DAYS = 30;
const MAX_INSTRUCTION_CHARS = 1000;

export interface InstructionRow {
  type: string;
  text: string;
}

interface Props {
  enabled: boolean;
  onToggle: (next: boolean) => void;
  leadDays: number[];
  onLeadDaysChange: (next: number[]) => void;
  instructions: InstructionRow[];
  onInstructionsChange: (next: InstructionRow[]) => void;
  clinicTimezoneSet: boolean;
  sendHourLabel: string;
  disabled?: boolean;
  onPreview: () => void;
}

const leadUnit = (d: number) =>
  d === 0 ? 'days before (the day of the visit)' : d === 1 ? 'day before the visit' : 'days before the visit';

/**
 * Appointment reminders (ADR-0046): on/off, a row per reminder, and client instructions per visit
 * type. Presentational: RemindersSettings owns the state so the page's one Save sends it all.
 */
const AppointmentRemindersCard: React.FC<Props> = ({
  enabled,
  onToggle,
  leadDays,
  onLeadDaysChange,
  instructions,
  onInstructionsChange,
  clinicTimezoneSet,
  sendHourLabel,
  disabled,
  onPreview,
}) => {
  const [typeDraft, setTypeDraft] = useState('');

  // The visit types the booking form offers: the clinic's appointment services.
  const { data: catalog = [] } = useQuery(
    ['service-catalog', 'appointment'],
    () => serviceCatalogService.getAll({ category: 'appointment' }),
    { enabled: !disabled },
  );
  const usedTypes = useMemo(
    () => new Set(instructions.map((r) => r.type.trim().toLowerCase())),
    [instructions],
  );
  const typeOptions = useMemo(
    () =>
      catalog
        .filter((item) => item.isActive && !usedTypes.has(item.name.trim().toLowerCase()))
        .map((item) => item.name),
    [catalog, usedTypes],
  );

  const addInstructionType = () => {
    const type = typeDraft.trim();
    if (type && !usedTypes.has(type.toLowerCase())) {
      onInstructionsChange([...instructions, { type, text: '' }]);
    }
    setTypeDraft('');
  };

  // Editable while switched off too, so a clinic can set it up before the first send.
  const off = !!disabled;

  return (
    <Card variant="outlined" sx={{ mb: 2 }} id="appointment-reminders-settings">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="subtitle1">Appointment reminders</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              An email before each booked visit with the date, time, visit type, vet and place.
            </Typography>
          </Box>
          <Switch
            checked={enabled}
            disabled={disabled || (!clinicTimezoneSet && !enabled)}
            onChange={(e) => onToggle(e.target.checked)}
            inputProps={{ 'aria-label': 'Appointment reminders on/off' }}
          />
        </Stack>

        {!clinicTimezoneSet && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Set your clinic&apos;s time zone in Practice settings first, so the emails show the right
            visit times.
          </Alert>
        )}

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          When to send
        </Typography>
        <ReminderDaysEditor
          days={leadDays}
          onChange={onLeadDaysChange}
          maxCount={MAX_LEADS}
          maxDays={MAX_LEAD_DAYS}
          // At least one stays: an empty list would send nothing while switched on.
          minCount={1}
          unit={leadUnit}
          rowLabel={(i) => `Reminder ${i + 1}`}
          fieldLabel={(i) => `Reminder ${i + 1}, days before the visit`}
          removeLabel={(i) => `Remove reminder ${i + 1}`}
          addLabel="Add a reminder"
          disabled={off}
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 2.5 }}>
          Up to {MAX_LEADS} reminders, each 0 to {MAX_LEAD_DAYS} days before the visit, sent at{' '}
          {sendHourLabel} clinic time (the send time is under Email settings). A visit booked after that
          time still gets its reminder the same day, until 8 PM. Cancelled, no-show and completed visits
          never get one, and a moved visit gets a new one for the new time.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Instructions by visit type
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
          Added to the reminder for that visit type, for example &quot;No food or water after
          midnight&quot; before a surgery.
        </Typography>
        {instructions.length > 0 && (
          <Stack spacing={1.5} sx={{ mb: 1.5 }}>
            {instructions.map((row, i) => (
              <Stack key={row.type.toLowerCase()} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={row.type}
                  value={row.text}
                  onChange={(e) =>
                    onInstructionsChange(
                      instructions.map((r, j) => (j === i ? { ...r, text: e.target.value } : r)),
                    )
                  }
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={off}
                  inputProps={{ maxLength: MAX_INSTRUCTION_CHARS }}
                  helperText={`${row.text.length}/${MAX_INSTRUCTION_CHARS}`}
                />
                <IconButton
                  aria-label={`Remove instructions for ${row.type}`}
                  onClick={() => onInstructionsChange(instructions.filter((_, j) => j !== i))}
                  disabled={off}
                  sx={{ mt: 1 }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Autocomplete
            freeSolo
            options={typeOptions}
            inputValue={typeDraft}
            onInputChange={(_, value) => setTypeDraft(value)}
            disabled={off}
            sx={{ minWidth: 260 }}
            renderInput={(params) => <TextField {...params} size="small" label="Visit type" />}
          />
          <Button size="small" onClick={addInstructionType} disabled={off || !typeDraft.trim()}>
            Add instructions
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">
            The preview shows your saved settings.
          </Typography>
          <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={onPreview} disabled={disabled} sx={{ flex: 'none', whiteSpace: 'nowrap' }}>
            Preview email
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AppointmentRemindersCard;
